/**
 * The cards of the latest-reported-day summary, in the order the page shows
 * them.
 *
 * The treatment chain is read here rather than in the Water Balance above: what
 * arrived at the two plants, what came out of them, and how that total splits
 * between the two. The balance keeps the sources, whose question is whether a
 * site took in as much as it used; these four answer what the plants did, which
 * is a different question asked by different people.
 *
 * No figure appears in both places. A card repeating one the balance also drew
 * would say it twice and say it differently — the balance reads one day where
 * every component reported, while each card picks its own latest reporting day,
 * so the two can disagree on the same quantity.
 *
 * `derived` marks the one figure no meter reports: the card computes it from
 * two KPIs and says so.
 *
 * `description` says in ordinary words what the figure is. The titles are the
 * plant's own names for these points — ETP outlet, STP treated — and they are
 * precise to anyone who works the plant and opaque to everyone else the
 * dashboard is shown to. The description is written for the second reader: no
 * abbreviations, no formulae, one sentence saying which water this is and where
 * it was on its way to.
 */
export const WATER_METRICS = [
  {
    id: 'sent-to-treatment',
    title: 'Sent to Treatment',
    description:
      'Used water arriving at the two plants to be cleaned, both inlets counted together.',
    group: 'treatment',
    kpi: ['STP (Untreated)', 'ETP (Untreated)'],
  },
  {
    id: 'final-treated',
    title: 'Final Treated Water',
    description:
      'Everything the two plants finished cleaning and sent back out, counted once.',
    group: 'treatment',
    kpi: 'Final Treated (STP + ETP)',
  },
  {
    id: 'treated-etp',
    title: 'Treated Water (ETP Outlet)',
    description:
      'Water the effluent treatment plant has finished cleaning and sent back out.',
    group: 'treatment',
    category: 'Treated Water',
    subCategory: 'Recycled',
    kpi: 'ETP',
  },
  {
    id: 'treated-stp',
    title: 'STP Treated Water',
    description:
      "The sewage plant's own share of the clean water, once the effluent plant's share is taken out.",
    group: 'treatment',
    kpi: ['ETP', 'Final Treated (STP + ETP)'],
    derived: 'Final Treated (STP + ETP) − ETP',
  },
];

/**
 * The group, and the heading it carries.
 *
 * The heading used to say "Today's Summary", which was rarely true. Each card
 * reads the most recent day its own KPI reported within a three-day window, so
 * a plant that last reported on Tuesday shows Tuesday — and two cards can sit
 * side by side showing different days. Every card names the day it is showing;
 * the heading's job is to stop contradicting them.
 */
export const METRIC_GROUPS = [
  { id: 'treatment', title: 'Latest Reported Day — Treatment' },
];
